import type { ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { NAV_SECTIONS } from '@crapplet/shared';
import { useAuth } from './lib/auth';
import { Shell, useVisibleSections } from './components/Shell';
import { Loading } from './components/ui';
import { LoginPage } from './pages/Login';
import { EnrollMfaPage } from './pages/EnrollMfa';
import { OverviewPage } from './pages/Overview';
import { CustomersPage } from './pages/Customers';
import { CustomerDetailPage } from './pages/CustomerDetail';
import { UsersPage } from './pages/Users';
import { RolesPage } from './pages/Roles';
import { AuditPage } from './pages/Audit';
import { SettingsPage } from './pages/Settings';
import { AccountPage } from './pages/Account';
import { NotFoundPage, PlannedPage } from './pages/Planned';
import { DatacentersPage, RoomsPage } from './pages/Sites';
import { FloorPlansPage } from './pages/FloorPlans';
import { RackDetailPage, RacksPage } from './pages/Racks';
import { HardwarePage } from './pages/Hardware';
import { DeviceDetailPage, DeviceLabelPage, DeviceLabelsPage } from './pages/DeviceDetail';
import { DeviceSheetPage, DeviceSheetsPage } from './pages/DeviceSheet';
import { NetworkPage } from './pages/Network';
import { NetworkDevicePage } from './pages/NetworkDevice';
import { IpamPage, PrefixDetailPage } from './pages/Ipam';
import { NetworkMonitoringPage } from './pages/NetworkMonitoring';
import { AlertsPage } from './pages/Alerts';
import { HealthPage } from './pages/Health';
import { PowerPage } from './pages/Power';
import { ImagesPage, ProvisioningPage } from './pages/Provisioning';
import { ProxmoxPage, VirtualizorPage } from './pages/Virtualization';
import { VirtualMachinePage } from './pages/VirtualMachine';
import { ColocationPage } from './pages/Colocation';
import { ServicesPage } from './pages/Services';
import { TicketDetailPage, TicketsPage } from './pages/Tickets';
import { ApiIntegrationsPage } from './pages/ApiIntegrations';
import { WorkflowsPage } from './pages/Workflows';
import { ReportsPage } from './pages/Reports';
import { BillingPage } from './pages/Billing';
import { MaintenancePage } from './pages/Maintenance';

/** Implemented sections. Every key here must be `available` in NAV_SECTIONS (checked by a test). */
export const IMPLEMENTED: Record<string, ComponentType> = {
  overview: OverviewPage,
  customers: CustomersPage,
  users: UsersPage,
  audit: AuditPage,
  settings: SettingsPage,
  datacenters: DatacentersPage,
  rooms: RoomsPage,
  'floor-plans': FloorPlansPage,
  racks: RacksPage,
  hardware: HardwarePage,
  network: NetworkPage,
  ipam: IpamPage,
  'network-monitoring': NetworkMonitoringPage,
  alerts: AlertsPage,
  health: HealthPage,
  power: PowerPage,
  provisioning: ProvisioningPage,
  images: ImagesPage,
  proxmox: ProxmoxPage,
  virtualizor: VirtualizorPage,
  colocation: ColocationPage,
  orders: ServicesPage,
  tickets: TicketsPage,
  maintenance: MaintenancePage,
  workflows: WorkflowsPage,
  reports: ReportsPage,
  billing: BillingPage,
  api: ApiIntegrationsPage,
};

function AppRoutes() {
  const { can, me } = useAuth();
  const visible = new Set(useVisibleSections().map((s) => s.key));
  return (
    <Shell>
      <Routes>
        {NAV_SECTIONS.map((s) => {
          if (!visible.has(s.key)) return null;
          const Page = IMPLEMENTED[s.key];
          return <Route key={s.key} path={s.path} element={Page && s.status === 'available' ? <Page /> : <PlannedPage section={s} />} />;
        })}
        {can('roles.read') && <Route path="/roles" element={<RolesPage />} />}
        {visible.has('racks') && <Route path="/racks/:id" element={<RackDetailPage />} />}
        {visible.has('hardware') && <Route path="/hardware/labels" element={<DeviceLabelsPage />} />}
        {visible.has('hardware') && <Route path="/hardware/:id" element={<DeviceDetailPage />} />}
        {visible.has('hardware') && <Route path="/hardware/:id/label" element={<DeviceLabelPage />} />}
        {visible.has('hardware') && <Route path="/hardware/sheets" element={<DeviceSheetsPage />} />}
        {visible.has('hardware') && <Route path="/hardware/:id/sheet" element={<DeviceSheetPage />} />}
        {visible.has('network') && <Route path="/network/devices/:id" element={<NetworkDevicePage />} />}
        {visible.has('ipam') && <Route path="/ipam/prefixes/:id" element={<PrefixDetailPage />} />}
        {visible.has('tickets') && <Route path="/tickets/:id" element={<TicketDetailPage />} />}
        {visible.has('customers') && <Route path="/customers/:id" element={<CustomerDetailPage />} />}
        {visible.has('virtualizor') && <Route path="/integrations/virtualizor/vms/:id" element={<VirtualMachinePage kind="virtualizor" />} />}
        {visible.has('proxmox') && <Route path="/integrations/proxmox/vms/:id" element={<VirtualMachinePage kind="proxmox" />} />}
        <Route path="/account" element={<AccountPage />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Shell>
  );
}

export function App() {
  const { me, loading } = useAuth();
  if (loading) return <Loading label="Starting NexoraDC" />;
  if (!me) return <LoginPage />;
  if (me.mfaEnrollmentRequired) return <EnrollMfaPage />;
  return <AppRoutes />;
}
